/**
 * Generic, self-cleaning timer manager for "soft delete" / lazy-eviction UI
 * flows: an item goes disabled immediately, but stays visible for a grace
 * period so a fast reopen doesn't cause a visible flicker; only if the timer
 * actually fires does it get hidden. Reused for selections, markets, and
 * fixtures — each gets its own scheduler instance (own id namespace).
 *
 * Not React-specific: hold one instance per concern in a useRef() so it
 * survives re-renders without being recreated, and call cancelAll() in that
 * effect's cleanup so no timers leak across unmounts/reconnects.
 */
export const createEvictionScheduler = () => {
  const timers = new Map(); // id -> timeoutId

  /** (Re)schedules eviction for `id` after `delayMs`. Replaces any pending
   * timer for the same id — an item that re-locks before its previous timer
   * fired gets a fresh countdown, not two overlapping ones. */
  const schedule = (id, delayMs, onEvict) => {
    const existing = timers.get(id);
    if (existing != null) clearTimeout(existing);
    const timeoutId = setTimeout(() => {
      timers.delete(id);
      onEvict(id);
    }, Math.max(0, delayMs));
    timers.set(id, timeoutId);
  };

  /** Cancels a pending eviction — call this the moment an item reopens,
   * BEFORE it would have been hidden. Safe to call even if nothing is scheduled. */
  const cancel = (id) => {
    const existing = timers.get(id);
    if (existing != null) {
      clearTimeout(existing);
      timers.delete(id);
    }
  };

  const isPending = (id) => timers.has(id);

  const cancelAll = () => {
    timers.forEach((t) => clearTimeout(t));
    timers.clear();
  };

  return { schedule, cancel, isPending, cancelAll };
};
